import { SHEET_FORMATS, type SheetFormat } from '@porotutu/files/sheet'
import { Button } from '@porotutu/react/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@porotutu/react/components/ui/dropdown-menu'
import { useT } from '@porotutu/react/session'
import { DownloadIcon } from 'lucide-react'
import { useState } from 'react'
import { notifyRefused } from '../notify'
import { downloadSheet } from './download'

export type ExportMenuProps<Scope extends string> = {
  scope: string
  scopes: readonly Scope[]
  href: (scope: Scope, format: SheetFormat) => string
}

export function ExportMenu<Scope extends string>({ scope, scopes, href }: ExportMenuProps<Scope>) {
  const t = useT()
  const [pending, setPending] = useState(false)

  const save = async (value: Scope, format: SheetFormat) => {
    setPending(true)

    const refusal = await downloadSheet(href(value, format), `export.${format}`)

    setPending(false)

    if (refusal !== null) notifyRefused(t, refusal)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="outline" size="sm" disabled={pending}>
            <DownloadIcon />
            {t(`${scope}.button`)}
          </Button>
        }
      />
      <DropdownMenuContent align="end">
        {scopes.map((value, index) => (
          <DropdownMenuGroup key={value}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{t(`${scope}.scope.${value}`)}</DropdownMenuLabel>
            {SHEET_FORMATS.map((format) => (
              <DropdownMenuItem
                key={format}
                data-href={href(value, format)}
                onClick={() => {
                  void save(value, format)
                }}
              >
                {t(`${scope}.format.${format}`)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
